/**
 * findInsertValuesTarget
 *
 * Detect whether the cursor sits inside a `VALUES (…)` element of an INSERT
 * that has an EXPLICIT column list, and if so resolve which declared column
 * the element positionally maps to ("positively typed"). Companion to
 * `findTypedInsertColumns` (which solves a different problem: cursor inside
 * the COLUMN LIST itself) — same tokenizer, same conventions.
 *
 * Returns `null` when:
 *   - there's no enclosing paren at all,
 *   - the enclosing paren isn't a VALUES row (subquery / function-call /
 *     other tuple parens all reject naturally — the walk-back requires the
 *     paren to be immediately preceded, after skipping complete prior
 *     `, ( … )` row groups, by the `VALUES`/`VALUE` keyword),
 *   - the INSERT has no explicit column list (`VALUES` directly after the
 *     table name),
 *   - the element index has no corresponding declared column.
 *
 * Used by per-engine resolvers (T18b) to emit type-aware value candidates
 * (enum values / NULL / DEFAULT / scalar routines) instead of the flooded
 * generic VALUES-position emission, for exactly the elements whose target
 * column is statically known.
 */

import { tokenize, type Token } from './tokenizer';
import type { DatabaseEngineId } from '@/core/engines';

export type InsertValuesTarget = {
  readonly columnName: string;
  readonly table: { readonly name: string; readonly schema?: string };
};

const INSERT_MODIFIER_KEYWORDS = new Set([
  'OR',
  'IGNORE',
  'REPLACE',
  'ROLLBACK',
  'ABORT',
  'FAIL',
  'LOW_PRIORITY',
  'HIGH_PRIORITY',
  'DELAYED',
]);

export function findInsertValuesTarget(
  source: string,
  cursorOffset: number,
  statementStart: number,
  statementEnd: number,
  engine: DatabaseEngineId,
): InsertValuesTarget | null {
  const tokens = tokenize(source, statementStart, statementEnd, engine);

  // Locate the token at-or-just-before the cursor (same convention as
  // findTypedInsertColumns).
  let cursorTokenIdx = -1;
  for (let i = 0; i < tokens.length; i += 1) {
    if (tokens[i].start >= cursorOffset) break;
    cursorTokenIdx = i;
  }
  if (cursorTokenIdx < 0) return null;

  // Walk back, counting paren depth, to find the unmatched `(` that opens
  // the enclosing VALUES-row element list. Depth-0 commas encountered along
  // the way (before hitting that `(`) count the elements already committed
  // before the cursor — i.e. the 0-based element index.
  let depth = 0;
  let lparenIdx = -1;
  let elementIndex = 0;
  for (let i = cursorTokenIdx; i >= 0; i -= 1) {
    const t = tokens[i];
    if (t.type === 'semicolon') return null;
    if (t.type === 'rparen') {
      depth += 1;
      continue;
    }
    if (t.type === 'lparen') {
      if (depth === 0) {
        lparenIdx = i;
        break;
      }
      depth -= 1;
      continue;
    }
    if (t.type === 'comma' && depth === 0) elementIndex += 1;
  }
  if (lparenIdx < 0) return null;

  // Walk further back past zero or more complete prior row groups
  // (`, ( … )` — multi-row VALUES) until the token immediately before is the
  // `VALUES` / `VALUE` keyword. Anything else (a bare `(`, an identifier, …)
  // means the paren we found isn't a VALUES row at all — subquery / function
  // / other tuple parens reject here.
  let k = lparenIdx - 1;
  while (k >= 0 && tokens[k].type === 'comma') {
    k -= 1; // consume the comma separating this row from the previous one
    if (k < 0 || tokens[k].type !== 'rparen') return null;
    let rowDepth = 1;
    k -= 1;
    while (k >= 0 && rowDepth > 0) {
      if (tokens[k].type === 'rparen') rowDepth += 1;
      else if (tokens[k].type === 'lparen') rowDepth -= 1;
      k -= 1;
    }
    if (rowDepth !== 0) return null; // unbalanced — malformed input
  }
  if (k < 0) return null;
  const valuesKw = identUpper(tokens[k]);
  if (valuesKw !== 'VALUES' && valuesKw !== 'VALUE') return null;

  // Immediately before VALUES must be the `)` closing an EXPLICIT column
  // list. `VALUES` right after the table name (no column list) rejects.
  const colListCloseIdx = k - 1;
  if (colListCloseIdx < 0 || tokens[colListCloseIdx].type !== 'rparen') return null;
  const colListOpenIdx = findMatchingOpenParen(tokens, colListCloseIdx);
  if (colListOpenIdx < 0) return null;

  const columnList = extractColumnList(tokens, colListOpenIdx, colListCloseIdx);
  if (!columnList || elementIndex >= columnList.length) return null;

  // Before the column-list `(` expect the qualified table name (1-3 dotted
  // parts), then `INTO`, then `INSERT`/`REPLACE` modulo dialect modifiers —
  // identical shape to findTypedInsertColumns.
  let m = colListOpenIdx - 1;
  if (m < 0 || (tokens[m].type !== 'ident' && tokens[m].type !== 'kw')) return null;
  const tableTok = tokens[m];
  m -= 1;
  let schemaTok: Token | undefined;
  for (let dotted = 0; dotted < 2; dotted += 1) {
    if (m < 0 || tokens[m].type !== 'dot') break;
    m -= 1;
    if (m < 0 || (tokens[m].type !== 'ident' && tokens[m].type !== 'kw')) return null;
    if (dotted === 0) schemaTok = tokens[m];
    m -= 1;
  }
  if (m < 0 || identUpper(tokens[m]) !== 'INTO') return null;
  m -= 1;
  while (m >= 0) {
    const u = identUpper(tokens[m]);
    if (u === 'INSERT' || u === 'REPLACE') break;
    if (u !== null && INSERT_MODIFIER_KEYWORDS.has(u)) {
      m -= 1;
      continue;
    }
    return null;
  }
  if (m < 0) return null;

  const table = schemaTok
    ? { name: stripIdentQuotes(tableTok.text), schema: stripIdentQuotes(schemaTok.text) }
    : { name: stripIdentQuotes(tableTok.text) };
  return { columnName: columnList[elementIndex]!, table };
}

/** Walk backward from a `)` at `closeIdx` to its matching `(`, returning -1
 *  on imbalance. */
function findMatchingOpenParen(tokens: readonly Token[], closeIdx: number): number {
  let depth = 1;
  let i = closeIdx - 1;
  while (i >= 0 && depth > 0) {
    if (tokens[i].type === 'rparen') depth += 1;
    else if (tokens[i].type === 'lparen') depth -= 1;
    if (depth === 0) return i;
    i -= 1;
  }
  return -1;
}

/** Extract the ordered, comma-separated bare identifiers strictly between
 *  `openIdx` and `closeIdx`. `null` on anything else (qualified names,
 *  expressions, empty list) — INSERT column lists are always a flat,
 *  comma-separated identifier list. */
function extractColumnList(
  tokens: readonly Token[],
  openIdx: number,
  closeIdx: number,
): readonly string[] | null {
  const cols: string[] = [];
  let i = openIdx + 1;
  while (i < closeIdx) {
    const t = tokens[i];
    if (t.type !== 'ident' && t.type !== 'kw') return null;
    cols.push(stripIdentQuotes(t.text));
    i += 1;
    if (i >= closeIdx) break;
    if (tokens[i].type !== 'comma') return null;
    i += 1;
  }
  return cols.length > 0 ? cols : null;
}

function identUpper(t: Token | undefined): string | null {
  if (!t) return null;
  if (t.type !== 'ident' && t.type !== 'kw') return null;
  return stripIdentQuotes(t.text).toUpperCase();
}

function stripIdentQuotes(s: string): string {
  if (s.length < 2) return s;
  const first = s[0];
  const last = s[s.length - 1];
  if (first === '`' && last === '`') return s.slice(1, -1);
  if (first === '"' && last === '"') return s.slice(1, -1);
  if (first === '[' && last === ']') return s.slice(1, -1);
  return s;
}
